import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getServiceBySlug, SERVICES } from "@/data/services";
import { CATEGORIES, SITE } from "@/data/config";
import { Overline, PrimaryButton, SecondaryButton, Crumbs, Tag } from "@/components/Primitives";
import RelatedContent from "@/components/RelatedContent";
import { resolveServiceCta } from "@/lib/cta";
import { billingLabel } from "@/lib/billing";
import { resolvePricing } from "@/lib/pricing";
import { categoryHref } from "@/lib/categoryRoute";
import { CATEGORY_EDUCATION, SERVICE_EDUCATION, learnMoreForCategory, CTA_CONTEXT } from "@/data/educational";
import CTABlock from "@/components/CTABlock";
import LearnMoreCard from "@/components/LearnMoreCard";
import PricingFAQ from "@/components/PricingFAQ";

const Block = ({ title, items, dark = true, testId }) => (
  <div data-testid={testId} className={`p-6 lg:p-8 border ${dark ? "bg-black text-white border-white/15" : "bg-black text-white border-[#FFC600]/40"}`}>
    <Overline color={dark ? "yellow" : "yellow"} className="mb-4">{title}</Overline>
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3">
          <span className="mt-2 inline-block w-2 h-2 bg-[#FFC600]" />
          <span className="text-sm lg:text-base leading-snug text-white/90">{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const navigate = useNavigate();

  useSeo({
    title: service ? `${service.name} | Kostaja` : "Service | Kostaja",
    description: service ? service.outcome : "Kostaja service",
  });

  if (!service) {
    return (
      <main data-testid="service-not-found" className="bg-black">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-24 text-center">
          <Overline className="mb-4">404</Overline>
          <h1 className="text-5xl font-black tracking-tighter">Service not found.</h1>
          <p className="mt-4 text-white/70">It may have been renamed or moved.</p>
          <div className="mt-8 flex justify-center gap-3">
            <SecondaryButton to="/services">All services</SecondaryButton>
            <PrimaryButton to="/contact">Contact</PrimaryButton>
          </div>
        </div>
      </main>
    );
  }

  const cat = CATEGORIES.find((c) => c.slug === service.category);
  const cta = resolveServiceCta(service);
  const pricing = resolvePricing(service);
  const ctaLabel = cta.label || service.cta?.label;
  const catEdu = CATEGORY_EDUCATION[service.category] || {};
  const svcEdu = SERVICE_EDUCATION[service.slug] || {};
  const edu = {
    whatThisIs: catEdu.whatThisIs,
    whyItMatters: catEdu.whyItMatters,
    whatStrongerLooks: catEdu.whatStrongerLooks,
    signs: svcEdu.signs || catEdu.signs || [],
    commonMistakes: svcEdu.commonMistakes || catEdu.commonMistakes || [],
    howToUse: svcEdu.howToUse || catEdu.howToUse,
    whenNot: svcEdu.whenNot || catEdu.whenNot,
  };
  const learnMore = learnMoreForCategory(service.category);
  const ctaContext = CTA_CONTEXT[service.cta?.kind] || CTA_CONTEXT.contact;

  const peers = SERVICES.filter(
    (s) => s.category === service.category && s.slug !== service.slug
  ).slice(0, 4);

  return (
    <main data-testid="service-detail" className="bg-black">
      {/* HEAD */}
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-10 lg:py-16">
          <Crumbs
            items={[
              { to: "/", label: "Home" },
              { to: "/services", label: "Services" },
              { to: categoryHref(cat?.slug), label: cat?.name },
              { label: service.name },
            ]}
          />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <Overline color="yellow" className="mb-4">{cat?.name}</Overline>
              <h1 className="text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tighter leading-[0.92]">
                {service.name}
              </h1>
              <p className="mt-6 text-lg lg:text-2xl text-white/85 max-w-3xl">{service.outcome}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {(service.tags || []).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 border border-white/30 text-white/80">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="border-2 border-[#FFC600] p-6 lg:p-7 bg-black">
                <div className="k-overline text-[#FFC600]">
                  {pricing.showPublicPrice ? "Price" : "The offer"}
                </div>
                {pricing.showPublicPrice ? (
                  <>
                    <div className="text-4xl lg:text-5xl font-black tracking-tighter mt-1">{service.priceLabel}</div>
                    <div className="mt-1 text-white/60 text-xs uppercase tracking-[0.2em]">
                      {billingLabel(service.billing, "long")}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-2xl lg:text-3xl font-black tracking-tight mt-1 leading-tight">
                      {pricing.engagementType}
                    </div>
                    <div className="mt-3 text-white/85 text-sm leading-relaxed">
                      {pricing.displayLabel}
                    </div>
                    <div className="mt-2 text-white/55 text-[10px] uppercase tracking-[0.2em]">
                      {billingLabel(service.billing, "long")}
                    </div>
                  </>
                )}
                <div className="mt-6 flex flex-col gap-3">
                  {cta.isInternal ? (
                    <PrimaryButton to={cta.href} testId="service-primary-cta">{ctaLabel}</PrimaryButton>
                  ) : (
                    <PrimaryButton href={cta.href} target={cta.target} testId="service-primary-cta">{ctaLabel}</PrimaryButton>
                  )}
                  <SecondaryButton href={SITE.bookingUrl} target="_blank" testId="service-book" dark>
                    Start a Project
                  </SecondaryButton>
                </div>
                {!service.stripePaymentLink && service.cta.kind === "stripe" && pricing.showPublicPrice ? (
                  <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/50">
                    Payment link arriving soon. Use the contact step to start.
                  </p>
                ) : null}
                <p className="mt-4 text-white/70 text-xs leading-relaxed border-t border-white/15 pt-4">
                  {pricing.showPublicPrice
                    ? ctaContext
                    : "Scoped after a short review. Kostaja confirms scope, timeline, deliverables, and pricing structure before larger work begins."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BEST FOR */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-3">Best for</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">{service.bestFor}</h2>
          </div>
          <div className="lg:col-span-7 grid gap-6">
            <Block title="What you receive" items={service.whatYouReceive} testId="block-receive" />
            <Block title="What this helps fix" items={service.whatThisHelpsFix} testId="block-fix" />
          </div>
        </div>
      </section>

      {/* SCOPE PROTECTION + INPUTS */}
      <section className="bg-black text-white border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Block title="What is not included" items={service.notIncluded} dark testId="block-not-included" />
          <Block title="What we need from you" items={service.whatWeNeed} dark testId="block-need" />
        </div>
      </section>

      {/* WHY IT MATTERS + STRONGER LOOKS */}
      {(edu.whatThisIs || edu.whyItMatters || edu.whatStrongerLooks) ? (
        <section data-testid="value-why-matters" className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Overline color="yellow" className="mb-3">Why this matters</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">{edu.whatThisIs}</h2>
            </div>
            <div className="lg:col-span-7 grid gap-5">
              <div className="border border-white/15 p-6 lg:p-8">
                <Overline color="yellow" className="mb-2">Why it matters commercially</Overline>
                <p className="text-white/85 text-base leading-relaxed">{edu.whyItMatters}</p>
              </div>
              <div className="border border-white/15 p-6 lg:p-8">
                <Overline color="yellow" className="mb-2">What a stronger version looks like</Overline>
                <p className="text-white/85 text-base leading-relaxed">{edu.whatStrongerLooks}</p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* SIGNS YOU MAY NEED THIS */}
      {edu.signs.length ? (
        <section data-testid="value-signs" className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Overline color="yellow" className="mb-3">Signs you may need this</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">If two or three of these are true, this service is the right call.</h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/15">
                {edu.signs.map((s) => (
                  <li key={s} className="bg-black p-5 flex items-start gap-3">
                    <span className="mt-2 inline-block w-2 h-2 bg-[#FFC600]" />
                    <span className="text-white/90 text-sm leading-snug">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ) : null}

      {/* COMMON MISTAKES + HOW TO USE + WHEN NOT */}
      <section data-testid="value-mistakes" className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {edu.commonMistakes.length ? (
            <div className="border border-white/15 p-6 lg:p-8">
              <Overline color="yellow" className="mb-3">Common mistakes this avoids</Overline>
              <ul className="space-y-2 text-white/85 text-sm leading-snug">
                {edu.commonMistakes.map((m) => <li key={m} className="flex gap-2"><span className="text-[#FFC600] shrink-0">·</span><span>{m}</span></li>)}
              </ul>
            </div>
          ) : null}
          {edu.howToUse ? (
            <div className="border border-white/15 p-6 lg:p-8">
              <Overline color="yellow" className="mb-3">How to use what you receive</Overline>
              <p className="text-white/85 text-sm leading-relaxed">{edu.howToUse}</p>
            </div>
          ) : null}
          {edu.whenNot ? (
            <div className="border border-[#FFC600]/40 p-6 lg:p-8 bg-black">
              <Overline color="yellow" className="mb-3">When not to buy this</Overline>
              <p className="text-white/85 text-sm leading-relaxed">{edu.whenNot}</p>
            </div>
          ) : null}
        </div>
      </section>

      {/* LEARN MORE BEFORE YOU DECIDE */}
      {learnMore.length ? (
        <section data-testid="learn-more" className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
              <div>
                <Overline color="yellow" className="mb-3">Learn more before you decide</Overline>
                <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">Useful before the first call.</h2>
              </div>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
              {learnMore.map((l) => (
                <LearnMoreCard key={l.slug} item={l} testId={`learn-more-${l.slug}`} />
              ))}
            </ul>
          </div>
        </section>
      ) : null}



      {/* TIMELINE + PROCESS */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Overline className="mb-3">Timeline</Overline>
            <p className="text-2xl lg:text-3xl font-bold leading-snug tracking-tight">{service.timeline}</p>
          </div>
          <div className="lg:col-span-7">
            <Overline className="mb-3">How the process works</Overline>
            <ol className="border-l border-white/25">
              {service.process.map((step, idx) => (
                <li key={step} data-testid={`process-step-${idx}`} className="pl-6 pb-6 relative">
                  <span className="absolute -left-[9px] top-1 inline-block w-4 h-4 k-bg-yellow border border-white/15" />
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/60">Step 0{idx + 1}</div>
                  <div className="mt-1 text-lg lg:text-xl font-bold">{step}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* PEER SERVICES */}
      {peers.length ? (
        <section className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
            <div className="flex items-end justify-between mb-8">
              <div>
                <Overline className="mb-3">More in {cat?.name}</Overline>
                <h2 className="text-3xl lg:text-5xl font-black tracking-tighter">Look at the neighbours.</h2>
              </div>
              <Link to={categoryHref(cat?.slug)} className="text-sm font-semibold underline underline-offset-4 hover:text-[#FFC600]">
                View all →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {peers.map((p) => {
                const peerPricing = resolvePricing(p);
                return (
                  <Link
                    key={p.slug}
                    to={`/services/${p.slug}`}
                    data-testid={`peer-${p.slug}`}
                    className="block p-5 border border-white/15 hover:k-hard-shadow-yellow transition-shadow"
                  >
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">{cat?.name}</div>
                    <div className="mt-1 text-lg font-black tracking-tighter">{p.name}</div>
                    <div className="mt-2 text-xs text-white/70 line-clamp-3">{p.outcome}</div>
                    <div className="mt-4 text-sm font-semibold">{peerPricing.displayLabel}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      <RelatedContent
        serviceSlugs={service.related?.services || []}
        needSlugs={service.related?.needs || []}
        industrySlugs={service.related?.industries || []}
        heading="Related paths"
      />

      {/* HOW PRICING IS SCOPED (proposal-based services only) */}
      {!pricing.showPublicPrice ? (
        <section data-testid="pricing-scoped" className="bg-black border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Overline color="yellow" className="mb-3">How pricing is scoped</Overline>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
                Scoped around the outcome, not a stock package.
              </h2>
              <p className="mt-6 text-white/75 text-base leading-relaxed max-w-md">
                Most Kostaja projects are scoped after a short review because the right price depends on what needs to change, how much already exists, and how directly the work supports the next business outcome.
              </p>
            </div>
            <div className="lg:col-span-7 grid gap-6">
              <div className="border border-white/15 p-6 lg:p-8">
                <Overline color="yellow" className="mb-3">What affects scope</Overline>
                <ul className="space-y-2">
                  {pricing.scopeFactors.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-2 inline-block w-2 h-2 bg-[#FFC600] shrink-0" />
                      <span className="text-white/90 text-sm lg:text-base leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border border-[#FFC600]/40 p-6 lg:p-8 bg-black">
                <Overline color="yellow" className="mb-3">What happens before pricing is confirmed</Overline>
                <ol className="space-y-3">
                  {[
                    "Kostaja reviews the current business presence.",
                    "The real issue is identified.",
                    "The right level of help is recommended.",
                    "Scope, timeline, deliverables, and pricing structure are confirmed.",
                    "Work begins only after the path is clear.",
                  ].map((step, idx) => (
                    <li key={step} className="flex items-start gap-3">
                      <span className="mt-0.5 shrink-0 text-[#FFC600] text-sm font-bold tracking-tight w-6">0{idx + 1}</span>
                      <span className="text-white/90 text-sm lg:text-base leading-snug">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <PricingFAQ compact testId="service-pricing-faq" />

      <CTABlock variant="service" />
    </main>
  );
};

export default ServiceDetail;